import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { myliApi, restaurantsApi } from '../lib/api';
import { Sidebar } from '../components/layout/Sidebar';
import { Header } from '../components/layout/Header';
import { RestaurantSelector } from '../components/dashboard/RestaurantSelector';
import { 
    Star, TrendingUp, TrendingDown, MessageSquare, ThumbsUp, ThumbsDown, 
    Minus, User, ExternalLink, RefreshCw, Filter,
    BarChart3, AlertCircle, Clock, Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
    BarChart,
    Bar,
    Legend
} from 'recharts';

// Restaurants à exclure de Myli
const EXCLUDED_LOCATIONS = ['Mamma Mia', 'Mamma mia'];

// Mapping entre noms Myli et noms de nos restaurants
const MYLI_TO_RESTAURANT_MAP = {
    'Coup de Bambou': 'Coup de bambou',
    'Jacques Balma': 'Jacques Balma',
    'Jacques Blagnac': 'Jacques Blagnac',
    'La Compagnie Française Busca': 'Compagnie Busca',
    'La Compagnie Française Jeanne d\'Arc': 'Compagnie Française',
    'Le Café Chouchou': 'Café Chouchou',
    'Ma biche sur le toit': 'Ma biche sur le toit',
    'Sous les pins': 'Sous les pins'
};

// Icônes des plateformes
const PublisherIcon = ({ name, size = 'md' }) => {
    const iconMap = {
        'Google': '🔍',
        'Trip Advisor': '🦉',
        'TripAdvisor': '🦉',
        'The Fork': '🍴',
        'TheFork': '🍴',
        'Facebook': '📘',
        'Yelp': '⭐',
        'Uber Eats': '🥡',
        'Zenchef': '📅',
        'Privateaser': '🎉'
    };
    const sizeClass = size === 'lg' ? 'text-3xl' : size === 'sm' ? 'text-sm' : 'text-lg';
    return <span className={sizeClass}>{iconMap[name] || '📝'}</span>;
};

// Affichage des étoiles
const StarRating = ({ rating, size = 'md' }) => {
    const fullStars = Math.floor(rating || 0);
    const hasHalf = (rating || 0) % 1 >= 0.5;
    const sizeClass = size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-6 h-6' : 'w-4 h-4';
    
    return (
        <div className="flex items-center gap-0.5">
            {[...Array(5)].map((_, i) => (
                <Star
                    key={i}
                    className={`${sizeClass} ${
                        i < fullStars 
                            ? 'fill-yellow-400 text-yellow-400' 
                            : i === fullStars && hasHalf
                                ? 'fill-yellow-400/50 text-yellow-400'
                                : 'text-gray-300'
                    }`}
                />
            ))}
            <span className={`ml-1 font-semibold ${size === 'lg' ? 'text-2xl' : size === 'sm' ? 'text-xs' : 'text-sm'}`}>
                {(rating || 0).toFixed(1)}
            </span>
        </div>
    );
};

// Carte statistique
const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => {
    const colorClasses = {
        blue: 'bg-blue-50 text-blue-600',
        green: 'bg-green-50 text-green-600',
        red: 'bg-red-50 text-red-600',
        yellow: 'bg-yellow-50 text-yellow-600',
        purple: 'bg-purple-50 text-purple-600'
    };
    
    return (
        <Card>
            <CardContent className="p-4">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-sm text-muted-foreground">{title}</p>
                        <p className="text-2xl font-bold mt-1">{value}</p>
                        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
                    </div>
                    <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
                        <Icon className="w-5 h-5" />
                    </div>
                </div>
                {trend !== undefined && trend !== 0 && (
                    <div className={`flex items-center gap-1 mt-2 text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        <span>{trend > 0 ? '+' : ''}{trend.toFixed(2)} vs mois précédent</span>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

// Distribution des notes
const RatingDistribution = ({ distribution }) => {
    const total = Object.values(distribution || {}).reduce((a, b) => a + b, 0);
    
    return (
        <div className="space-y-2">
            {[5, 4, 3, 2, 1].map(rating => {
                const count = distribution?.[rating] || 0;
                const percentage = total > 0 ? (count / total) * 100 : 0;
                
                return (
                    <div key={rating} className="flex items-center gap-2">
                        <span className="text-sm w-8 flex items-center gap-1">
                            {rating} <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                        </span>
                        <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                            <div 
                                className={`h-full rounded-full ${
                                    rating >= 4 ? 'bg-green-500' : rating === 3 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${percentage}%` }}
                            />
                        </div>
                        <span className="text-xs text-muted-foreground w-10 text-right">{count}</span>
                    </div>
                );
            })}
        </div>
    );
};

// Graphique d'évolution des notes dans le temps (année en cours seulement)
const RatingTrendChart = ({ reviews }) => {
    // Grouper les avis par mois de l'année en cours seulement
    const getMonthlyData = () => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth();
        
        // Créer les 12 mois de l'année avec des valeurs par défaut
        const monthlyStats = {};
        for (let i = 0; i <= currentMonth; i++) {
            const monthKey = `${currentYear}-${String(i + 1).padStart(2, '0')}`;
            monthlyStats[monthKey] = { ratings: [], count: 0 };
        }
        
        if (reviews && reviews.length > 0) {
            reviews.forEach(review => {
                if (review.published_at && review.rating) {
                    try {
                        const date = new Date(review.published_at);
                        if (!isNaN(date.getTime()) && date.getFullYear() === currentYear) {
                            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                            
                            if (monthlyStats[monthKey]) {
                                monthlyStats[monthKey].ratings.push(review.rating);
                                monthlyStats[monthKey].count++;
                            }
                        }
                    } catch (e) {
                        // Ignorer les dates invalides
                    }
                }
            });
        }
        
        // Convertir en tableau et calculer les moyennes
        const data = Object.entries(monthlyStats)
            .map(([month, stats]) => ({
                month,
                label: new Date(month + '-01').toLocaleDateString('fr-FR', { month: 'short' }),
                average: stats.ratings.length > 0 ? stats.ratings.reduce((a, b) => a + b, 0) / stats.ratings.length : null,
                count: stats.count
            }))
            .sort((a, b) => a.month.localeCompare(b.month));
        
        return data;
    };
    
    const data = getMonthlyData();
    const hasData = data.some(d => d.average !== null);
    
    if (!hasData) {
        return (
            <div className="h-48 flex items-center justify-center text-muted-foreground">
                Pas encore de données pour {new Date().getFullYear()}
            </div>
        );
    }
    
    return (
        <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                    dataKey="label" 
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                />
                <YAxis 
                    domain={[3, 5]} 
                    ticks={[3, 3.5, 4, 4.5, 5]}
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                />
                <Tooltip 
                    content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                            const d = payload[0].payload;
                            if (d.average === null) return null;
                            return (
                                <div className="bg-white border rounded-lg shadow-lg p-2 text-sm">
                                    <p className="font-medium">{d.label} {new Date().getFullYear()}</p>
                                    <p className="text-yellow-600">Note: {d.average.toFixed(2)} ★</p>
                                    <p className="text-muted-foreground">{d.count} avis</p>
                                </div>
                            );
                        }
                        return null;
                    }}
                />
                <ReferenceLine y={4.5} stroke="#22c55e" strokeDasharray="5 5" label={{ value: '4.5', position: 'right', fontSize: 10 }} />
                <Line 
                    type="monotone" 
                    dataKey="average" 
                    stroke="#eab308" 
                    strokeWidth={3}
                    dot={{ fill: '#eab308', strokeWidth: 2, r: 5 }}
                    activeDot={{ r: 7, fill: '#eab308' }}
                    connectNulls={true}
                />
            </LineChart>
        </ResponsiveContainer>
    );
};

// Histogramme des avis positifs vs négatifs par mois
const ReviewsHistogram = ({ reviews }) => {
    const getMonthlyData = () => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth();
        
        // Créer les mois de l'année en cours
        const monthlyStats = {};
        for (let i = 0; i <= currentMonth; i++) {
            const monthKey = `${currentYear}-${String(i + 1).padStart(2, '0')}`;
            monthlyStats[monthKey] = { positive: 0, negative: 0 };
        }
        
        if (reviews && reviews.length > 0) {
            reviews.forEach(review => {
                if (review.published_at && review.rating) {
                    try {
                        const date = new Date(review.published_at);
                        if (!isNaN(date.getTime()) && date.getFullYear() === currentYear) {
                            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                            
                            if (monthlyStats[monthKey]) {
                                // Convertir en nombre pour être sûr de la comparaison
                                const rating = parseFloat(review.rating);
                                if (!isNaN(rating) && rating >= 4) {
                                    monthlyStats[monthKey].positive++;
                                } else {
                                    monthlyStats[monthKey].negative++;
                                }
                            }
                        }
                    } catch (e) {
                        // Ignorer les dates invalides
                    }
                }
            });
        }
        
        // Convertir en tableau avec calcul du pourcentage de négatifs
        const data = Object.entries(monthlyStats)
            .map(([month, stats]) => {
                const total = stats.positive + stats.negative;
                const negativePercent = total > 0 ? Math.round((stats.negative / total) * 100) : 0;
                return {
                    month,
                    label: new Date(month + '-01').toLocaleDateString('fr-FR', { month: 'short' }),
                    positive: stats.positive,
                    negative: stats.negative,
                    total: total,
                    negativePercent: negativePercent
                };
            })
            .sort((a, b) => a.month.localeCompare(b.month));
        
        return data;
    };
    
    const data = getMonthlyData();
    const hasData = data.some(d => d.positive > 0 || d.negative > 0);
    
    if (!hasData) {
        return (
            <div className="h-48 flex items-center justify-center text-muted-foreground">
                Pas encore de données pour {new Date().getFullYear()}
            </div>
        );
    }
    
    // Label personnalisé pour afficher le % de négatifs au-dessus de chaque barre
    const renderCustomBarLabel = ({ x, y, width, value, index }) => {
        const item = data[index];
        if (!item || item.total === 0) return null;
        return (
            <text 
                x={x + width / 2} 
                y={y - 5} 
                fill="#ef4444" 
                textAnchor="middle" 
                fontSize={10}
                fontWeight="bold"
            >
                {item.negativePercent}%
            </text>
        );
    };
    
    return (
        <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data} margin={{ top: 25, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                    dataKey="label" 
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                />
                <YAxis 
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                />
                <Tooltip 
                    content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                            const d = payload[0].payload;
                            return (
                                <div className="bg-white border rounded-lg shadow-lg p-2 text-sm">
                                    <p className="font-medium">{d.label} {new Date().getFullYear()}</p>
                                    <p className="text-green-600">Positifs (4-5★): {d.positive}</p>
                                    <p className="text-red-600">Négatifs (&lt;4★): {d.negative}</p>
                                    <p className="text-muted-foreground">Total: {d.total}</p>
                                    <p className="text-red-700 font-semibold">% Négatifs: {d.negativePercent}%</p>
                                </div>
                            );
                        }
                        return null;
                    }}
                />
                <Legend 
                    formatter={(value) => value === 'positive' ? 'Positifs (4-5★)' : 'Négatifs (<4★)'}
                    wrapperStyle={{ fontSize: '11px' }}
                />
                <Bar dataKey="positive" fill="#22c55e" name="positive" radius={[4, 4, 0, 0]} />
                <Bar 
                    dataKey="negative" 
                    fill="#ef4444" 
                    name="negative" 
                    radius={[4, 4, 0, 0]}
                    label={renderCustomBarLabel}
                />
            </BarChart>
        </ResponsiveContainer>
    );
};

// Carte d'un avis
const ReviewCard = ({ review }) => {
    const [expanded, setExpanded] = useState(false);
    const content = review.content || '';
    const isLong = content.length > 200;
    
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            return new Date(dateStr).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });
        } catch {
            return dateStr;
        }
    };
    
    const getSentimentColor = (rating) => {
        if (rating >= 4) return 'border-l-green-500';
        if (rating >= 3) return 'border-l-yellow-500';
        return 'border-l-red-500';
    };
    
    return (
        <Card className={`border-l-4 ${getSentimentColor(review.rating)}`}>
            <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                        {review.author_avatar ? (
                            <img src={review.author_avatar} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                                <User className="w-4 h-4 text-gray-500" />
                            </div>
                        )}
                        <div>
                            <p className="font-medium text-sm">{review.author}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <PublisherIcon name={review.publisher} />
                                <span>{review.publisher}</span>
                                <span>•</span>
                                <span>{formatDate(review.published_at)}</span>
                            </div>
                        </div>
                    </div>
                    <StarRating rating={review.rating} size="sm" />
                </div>
                
                {review.title && (
                    <p className="font-medium text-sm mb-1">{review.title}</p>
                )}
                
                {content && (
                    <p className="text-sm text-gray-700">
                        {isLong && !expanded ? `${content.substring(0, 200)}...` : content}
                    </p>
                )}
                
                {isLong && (
                    <button 
                        onClick={() => setExpanded(!expanded)}
                        className="text-xs text-blue-600 mt-1 hover:underline"
                    >
                        {expanded ? 'Voir moins' : 'Voir plus'}
                    </button>
                )}
                
                {review.has_response && (
                    <div className="mt-3 pl-3 border-l-2 border-blue-200 bg-blue-50/50 p-2 rounded-r">
                        <p className="text-xs font-medium text-blue-800 mb-1">Réponse du propriétaire</p>
                        <p className="text-xs text-blue-700">{review.response_content}</p>
                    </div>
                )}
                
                {review.remote_link && (
                    <a 
                        href={review.remote_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-blue-600 mt-2 hover:underline"
                    >
                        Voir sur {review.publisher} <ExternalLink className="w-3 h-3" />
                    </a>
                )}
            </CardContent>
        </Card>
    );
};

// Page principale Marketing
const MarketingPage = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dashboardData, setDashboardData] = useState(null);
    const [groups, setGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [isConsolidatedView, setIsConsolidatedView] = useState(true);
    const [restaurants, setRestaurants] = useState([]);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [showNegativeOnly, setShowNegativeOnly] = useState(false);
    const [allLocations, setAllLocations] = useState([]); // Conserver toutes les locations pour le sélecteur
    const [selectedReviewMonth, setSelectedReviewMonth] = useState(() => {
        // Par défaut : mois en cours au format "YYYY-MM"
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const refreshIntervalRef = useRef(null);
    
    // Charger les restaurants de notre système
    useEffect(() => {
        const loadRestaurants = async () => {
            try {
                const response = await restaurantsApi.getAll();
                setRestaurants(response.data || []);
            } catch (err) {
                console.error('Error loading restaurants:', err);
            }
        };
        loadRestaurants();
    }, []);
    
    // Charger les groupes Myli au démarrage
    useEffect(() => {
        const loadGroups = async () => {
            try {
                const response = await myliApi.getGroups();
                setGroups(response.data.groups || []);
                if (response.data.groups?.length > 0) {
                    setSelectedGroupId(response.data.groups[0].id);
                }
            } catch (err) {
                console.error('Error loading groups:', err);
            }
        };
        loadGroups();
    }, []);
    
    // Sélectionner le premier restaurant accessible par défaut quand les locations sont chargées
    useEffect(() => {
        if (allLocations.length > 0 && !selectedLocation && isConsolidatedView && restaurants.length > 0) {
            // Trouver la première location Myli qui correspond à un restaurant accessible
            for (const loc of allLocations) {
                const mappedName = MYLI_TO_RESTAURANT_MAP[loc.name];
                const isAccessible = restaurants.some(r => 
                    r.name === mappedName || 
                    r.name.toLowerCase().includes(loc.name?.toLowerCase()) ||
                    loc.name?.toLowerCase().includes(r.name.toLowerCase())
                );
                if (isAccessible) {
                    setSelectedLocation(loc);
                    setIsConsolidatedView(false);
                    break;
                }
            }
        }
    }, [allLocations, selectedLocation, isConsolidatedView, restaurants]);
    
    // Charger le dashboard
    const loadDashboard = useCallback(async () => {
        if (!selectedGroupId && !selectedLocation) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const params = {};
            if (selectedLocation && !isConsolidatedView) {
                params.location_id = selectedLocation.id;
                // Aussi passer group_id pour le filtrage des reviews
                if (selectedGroupId) {
                    params.group_id = selectedGroupId;
                }
            } else if (selectedGroupId) {
                params.group_id = selectedGroupId;
            }
            
            const response = await myliApi.getDashboard(params);
            
            // Filtrer les locations exclues
            if (response.data.locations) {
                response.data.locations = response.data.locations.filter(
                    loc => !EXCLUDED_LOCATIONS.some(excluded => 
                        loc.name?.toLowerCase().includes(excluded.toLowerCase())
                    )
                );
                // Stocker les locations pour le sélecteur (uniquement en vue consolidée)
                if (isConsolidatedView && response.data.locations.length > 0) {
                    setAllLocations(response.data.locations);
                }
            }
            
            setDashboardData(response.data);
            setLastUpdate(new Date());
        } catch (err) {
            console.error('Error loading dashboard:', err);
            setError('Impossible de charger les données marketing');
        } finally {
            setLoading(false);
        }
    }, [selectedGroupId, selectedLocation, isConsolidatedView]);
    
    // Charger au démarrage et quand les paramètres changent
    useEffect(() => {
        if (selectedGroupId || selectedLocation) {
            loadDashboard();
        }
    }, [selectedGroupId, selectedLocation, isConsolidatedView, loadDashboard]);
    
    // Auto-refresh toutes les heures (3600000 ms)
    useEffect(() => {
        // Nettoyer l'intervalle précédent
        if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
        }
        
        // Configurer le nouvel intervalle (1 heure)
        refreshIntervalRef.current = setInterval(() => {
            console.log('🔄 Auto-refresh Marketing (toutes les heures)...');
            loadDashboard();
        }, 3600000); // 1 heure
        
        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
            }
        };
    }, [loadDashboard]);
    
    // Trouver le restaurant correspondant dans notre système
    const findMatchingRestaurant = (myliLocation) => {
        if (!myliLocation) return null;
        const mappedName = MYLI_TO_RESTAURANT_MAP[myliLocation.name];
        if (mappedName) {
            return restaurants.find(r => r.name === mappedName);
        }
        return restaurants.find(r => 
            r.name.toLowerCase().includes(myliLocation.name?.toLowerCase()) ||
            myliLocation.name?.toLowerCase().includes(r.name.toLowerCase())
        );
    };
    
    const handleRestaurantSelect = (restaurant) => {
        // Utiliser allLocations pour la recherche (qui conserve les locations même en vue individuelle)
        const locationsToSearch = allLocations.length > 0 ? allLocations : (dashboardData?.locations || []);
        
        // Trouver la location Myli correspondante via myliLocationId
        const myliLocation = locationsToSearch.find(loc => 
            loc.id === restaurant.myliLocationId
        );
        
        // Si pas trouvé par myliLocationId, chercher par nom
        if (!myliLocation) {
            const locationByName = locationsToSearch.find(loc => {
                const mappedName = MYLI_TO_RESTAURANT_MAP[loc.name];
                return mappedName === restaurant.name || 
                       loc.name?.toLowerCase().includes(restaurant.name.toLowerCase()) ||
                       restaurant.name.toLowerCase().includes(loc.name?.split(' ').slice(0, 2).join(' ').toLowerCase());
            });
            
            if (locationByName) {
                setSelectedLocation(locationByName);
                setIsConsolidatedView(false);
            }
            return;
        }
        
        setSelectedLocation(myliLocation);
        setIsConsolidatedView(false);
    };
    
    const handleBackToConsolidated = () => {
        setSelectedLocation(null);
        setIsConsolidatedView(true);
    };
    
    const stats = dashboardData?.stats || {};
    const reviews = dashboardData?.reviews || [];
    const locations = dashboardData?.locations || [];
    
    // Liste des noms de restaurants Myli autorisés pour cet utilisateur
    const allowedMyliNames = restaurants.length > 0
        ? Object.entries(MYLI_TO_RESTAURANT_MAP)
            .filter(([myliName, ourName]) => restaurants.some(r => r.name === ourName))
            .map(([myliName]) => myliName.toLowerCase())
        : null; // null = pas de filtrage (admin/direction)
    
    // Filtrer les reviews pour exclure Mamma Mia ET filtrer par restaurants de l'utilisateur
    // ET filtrer par le restaurant sélectionné si on n'est pas en vue consolidée
    const filteredReviews = reviews.filter(r => {
        // Exclure Mamma Mia
        if (EXCLUDED_LOCATIONS.some(excluded => 
            r.location_name?.toLowerCase().includes(excluded.toLowerCase())
        )) {
            return false;
        }
        
        // Si un restaurant spécifique est sélectionné (pas en vue consolidée)
        // Filtrer pour n'afficher que les avis de CE restaurant
        if (selectedLocation && !isConsolidatedView) {
            const selectedLocationName = selectedLocation.name?.toLowerCase() || '';
            const reviewLocationName = r.location_name?.toLowerCase() || '';
            // Vérifier si l'avis correspond au restaurant sélectionné
            return reviewLocationName.includes(selectedLocationName) ||
                   selectedLocationName.includes(reviewLocationName);
        }
        
        // En vue consolidée ou sans sélection: filtrer par restaurants de l'utilisateur
        if (allowedMyliNames) {
            return allowedMyliNames.some(name => 
                r.location_name?.toLowerCase().includes(name) ||
                name.includes(r.location_name?.toLowerCase() || '')
            );
        }
        return true;
    });
    
    // Utiliser allLocations pour le sélecteur (conserve les locations même en vue individuelle)
    const locationsForSelector = allLocations.length > 0 ? allLocations : locations;
    
    // Créer la liste de restaurants pour le sélecteur (mappés depuis Myli)
    // Et filtrer par les restaurants auxquels l'utilisateur a accès
    const mappedRestaurants = locationsForSelector.map(loc => {
        const matchingRestaurant = findMatchingRestaurant(loc);
        return {
            id: matchingRestaurant?.id || loc.id,
            name: matchingRestaurant?.name || MYLI_TO_RESTAURANT_MAP[loc.name] || loc.name,
            logo_url: matchingRestaurant?.logo_url || null,
            myliLocationId: loc.id // Garder l'ID Myli pour la sélection
        };
    }).filter(r => {
        // Filtrer par les restaurants de l'utilisateur (si l'utilisateur a des restaurants assignés)
        if (restaurants.length > 0) {
            return restaurants.some(userResto => userResto.name === r.name);
        }
        return true; // Admin/Direction voient tout
    });
    
    // Trouver le restaurant actuellement sélectionné (par ID Myli)
    const selectedRestaurantForSelector = selectedLocation 
        ? mappedRestaurants.find(r => r.myliLocationId === selectedLocation.id)
        : null;

    return (
        <div className="flex h-screen bg-background" data-testid="marketing-page">
            <Sidebar />
            
            <div className="flex-1 flex flex-col overflow-hidden">
                <Header 
                    title={isConsolidatedView ? 'Marketing - Avis Clients' : `Marketing - ${selectedLocation?.name || ''}`}
                />

                <main className="flex-1 overflow-auto p-6">
                    <div className="max-w-7xl mx-auto space-y-6">
                        
                        {/* Sélecteur de restaurant (comme sur ventes générales) */}
                        <section>
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-heading font-semibold">
                                    {isConsolidatedView ? 'Tous les restaurants (Consolidé)' : `${selectedLocation?.name || 'Restaurant sélectionné'}`}
                                </h2>
                                <div className="flex items-center gap-3">
                                    {lastUpdate && (
                                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            Maj: {lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    )}
                                    <Button onClick={loadDashboard} variant="outline" size="sm" disabled={loading}>
                                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                                    </Button>
                                </div>
                            </div>
                            
                            {/* Grille des restaurants avec logos (comme sur les autres pages) */}
                            {mappedRestaurants.length > 0 && (
                                <RestaurantSelector
                                    restaurants={mappedRestaurants}
                                    selectedId={selectedRestaurantForSelector?.id}
                                    onSelect={handleRestaurantSelect}
                                />
                            )}
                        </section>
                        
                        {loading && !dashboardData ? (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
                            </div>
                        ) : error ? (
                            <Card className="border-red-200 bg-red-50">
                                <CardContent className="p-6 text-center">
                                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                                    <h3 className="font-semibold text-red-800 mb-2">Erreur de chargement</h3>
                                    <p className="text-red-600 mb-4">{error}</p>
                                    <Button onClick={loadDashboard} variant="outline">
                                        <RefreshCw className="w-4 h-4 mr-2" />
                                        Réessayer
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            <>
                                {/* Notes actuelles (ce que le client voit sur internet) - Vue individuelle seulement */}
                                {!isConsolidatedView && dashboardData?.location?.publishers && (
                                    <Card className="mb-6">
                                        <CardHeader>
                                            <CardTitle className="text-base flex items-center gap-2">
                                                <Star className="w-4 h-4 text-yellow-500" />
                                                Notes actuelles
                                                <Badge variant="outline" className="ml-2 text-xs">Ce que le client voit</Badge>
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                                {dashboardData.location.publishers
                                                    .filter(pub => pub.globalScore !== null && pub.globalScore !== undefined)
                                                    .map(pub => (
                                                        <div key={pub.name} className="flex flex-col items-center p-3 bg-gray-50 rounded-lg">
                                                            <PublisherIcon name={pub.name} size="lg" />
                                                            <span className="text-sm font-medium mt-2">{pub.name}</span>
                                                            <div className="flex items-center gap-1 mt-1">
                                                                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                                                                <span className="text-xl font-bold">{pub.globalScore?.toFixed(1) || '-'}</span>
                                                            </div>
                                                            <span className="text-xs text-muted-foreground">
                                                                {pub.reviewsCount?.toLocaleString() || 0} avis
                                                            </span>
                                                        </div>
                                                    ))}
                                            </div>
                                            {dashboardData.location.publishers.filter(pub => pub.globalScore !== null).length === 0 && (
                                                <p className="text-sm text-muted-foreground text-center py-4">
                                                    Aucune note publique disponible
                                                </p>
                                            )}
                                        </CardContent>
                                    </Card>
                                )}
                                
                                {/* Statistiques globales */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <StatCard
                                        title={`Note moyenne (${new Date().getFullYear()})`}
                                        value={(stats.average_rating || 0).toFixed(1)}
                                        subtitle="sur 5 étoiles"
                                        icon={Star}
                                        color="yellow"
                                        trend={stats.recent_trend}
                                    />
                                    <StatCard
                                        title={`Total avis (${new Date().getFullYear()})`}
                                        value={stats.total_reviews || 0}
                                        subtitle={`${stats.recent_count || 0} ce mois`}
                                        icon={MessageSquare}
                                        color="blue"
                                    />
                                    <StatCard
                                        title={`Avis positifs (${new Date().getFullYear()})`}
                                        value={stats.sentiment?.positive || 0}
                                        subtitle={`${stats.total_reviews > 0 ? Math.round((stats.sentiment?.positive / stats.total_reviews) * 100) : 0}% du total`}
                                        icon={ThumbsUp}
                                        color="green"
                                    />
                                    <StatCard
                                        title={`Avis négatifs (${new Date().getFullYear()})`}
                                        value={stats.sentiment?.negative || 0}
                                        subtitle="À traiter en priorité"
                                        icon={ThumbsDown}
                                        color="red"
                                    />
                                </div>
                                
                                {/* Graphiques - Courbe des notes et Histogramme */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Évolution des notes dans le temps (année en cours) */}
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="text-base flex items-center gap-2">
                                                <TrendingUp className="w-4 h-4" />
                                                Évolution de la note moyenne ({new Date().getFullYear()})
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <RatingTrendChart reviews={filteredReviews} />
                                        </CardContent>
                                    </Card>
                                    
                                    {/* Histogramme des avis positifs vs négatifs */}
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="text-base flex items-center gap-2">
                                                <BarChart3 className="w-4 h-4" />
                                                Avis positifs vs négatifs ({new Date().getFullYear()})
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <ReviewsHistogram reviews={filteredReviews} />
                                        </CardContent>
                                    </Card>
                                </div>
                                
                                {/* Par plateforme et Sentiments */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Par plateforme (2026) */}
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="text-base flex items-center gap-2">
                                                <Filter className="w-4 h-4" />
                                                Par plateforme ({new Date().getFullYear()})
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="space-y-3">
                                                {Object.entries(stats.by_publisher || {}).map(([name, data]) => (
                                                    <div key={name} className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <PublisherIcon name={name} />
                                                            <span className="text-sm font-medium">{name}</span>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <span className="text-xs text-muted-foreground">{data.count} avis</span>
                                                            <StarRating rating={data.average || 0} size="sm" />
                                                        </div>
                                                    </div>
                                                ))}
                                                {Object.keys(stats.by_publisher || {}).length === 0 && (
                                                    <p className="text-sm text-muted-foreground text-center py-4">
                                                        Aucune donnée disponible
                                                    </p>
                                                )}
                                            </div>
                                        </CardContent>
                                    </Card>
                                    
                                    {/* Sentiment */}
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="text-base">Répartition des sentiments</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="space-y-4">
                                                {[
                                                    { key: 'positive', label: 'Positifs (4-5★)', color: 'bg-green-500', icon: ThumbsUp },
                                                    { key: 'neutral', label: 'Neutres (3★)', color: 'bg-yellow-500', icon: Minus },
                                                    { key: 'negative', label: 'Négatifs (1-2★)', color: 'bg-red-500', icon: ThumbsDown }
                                                ].map(({ key, label, color, icon: Icon }) => {
                                                    const count = stats.sentiment?.[key] || 0;
                                                    const total = stats.total_reviews || 1;
                                                    const percentage = (count / total) * 100;
                                                    
                                                    return (
                                                        <div key={key}>
                                                            <div className="flex items-center justify-between mb-1">
                                                                <span className="text-sm flex items-center gap-1">
                                                                    <Icon className="w-3 h-3" />
                                                                    {label}
                                                                </span>
                                                                <span className="text-sm font-medium">{count} ({percentage.toFixed(0)}%)</span>
                                                            </div>
                                                            <div className="w-full bg-gray-100 rounded-full h-2">
                                                                <div className={`h-2 rounded-full ${color}`} style={{ width: `${percentage}%` }} />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                                
                                {/* Liste des avis récents */}
                                <div>
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                                        <h2 className="text-lg font-semibold flex items-center gap-2">
                                            <MessageSquare className="w-5 h-5" />
                                            {showNegativeOnly ? 'Avis négatifs (< 4★)' : 'Avis récents'}
                                        </h2>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {/* Sélecteur de mois */}
                                            <Select
                                                value={selectedReviewMonth}
                                                onValueChange={setSelectedReviewMonth}
                                            >
                                                <SelectTrigger className="w-[180px]">
                                                    <Calendar className="w-4 h-4 mr-2" />
                                                    <SelectValue placeholder="Sélectionner un mois" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {(() => {
                                                        const currentYear = new Date().getFullYear();
                                                        const currentMonth = new Date().getMonth();
                                                        const months = [];
                                                        
                                                        // Générer les mois de janvier au mois en cours
                                                        for (let i = 0; i <= currentMonth; i++) {
                                                            const monthValue = `${currentYear}-${String(i + 1).padStart(2, '0')}`;
                                                            const monthLabel = new Date(currentYear, i, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
                                                            months.push({ value: monthValue, label: monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1) });
                                                        }
                                                        
                                                        return months.map(month => (
                                                            <SelectItem key={month.value} value={month.value}>
                                                                {month.label}
                                                            </SelectItem>
                                                        ));
                                                    })()}
                                                </SelectContent>
                                            </Select>
                                            
                                            {/* Bouton filtre négatifs */}
                                            <Button
                                                variant={showNegativeOnly ? "destructive" : "outline"}
                                                size="sm"
                                                onClick={() => setShowNegativeOnly(!showNegativeOnly)}
                                                className="gap-2"
                                            >
                                                <ThumbsDown className="w-4 h-4" />
                                                {showNegativeOnly ? 'Voir tous' : 'Avis négatifs'}
                                            </Button>
                                        </div>
                                    </div>
                                    
                                    {(() => {
                                        // Filtrer par mois sélectionné
                                        let reviewsForMonth = filteredReviews.filter(r => {
                                            if (!r.published_at) return false;
                                            const reviewMonth = r.published_at.substring(0, 7);
                                            return reviewMonth === selectedReviewMonth;
                                        });
                                        
                                        // Puis filtrer par négatif si activé
                                        const displayedReviews = showNegativeOnly 
                                            ? reviewsForMonth.filter(r => {
                                                // Convertir en nombre pour être sûr de la comparaison
                                                const rating = parseFloat(r.rating);
                                                return !isNaN(rating) && rating < 4;
                                            })
                                            : reviewsForMonth;
                                        
                                        // Badge avec le nombre d'avis
                                        const monthName = new Date(selectedReviewMonth + '-01').toLocaleDateString('fr-FR', { month: 'long' });
                                        
                                        return (
                                            <>
                                                <div className="mb-4 flex items-center gap-2">
                                                    <Badge variant="secondary">
                                                        {displayedReviews.length} avis
                                                    </Badge>
                                                    <span className="text-sm text-muted-foreground">
                                                        pour {monthName} {new Date().getFullYear()}
                                                        {showNegativeOnly && ' (négatifs uniquement)'}
                                                    </span>
                                                </div>
                                                
                                                {displayedReviews.length > 0 ? (
                                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                                        {displayedReviews.slice(0, 50).map(review => (
                                                            <ReviewCard key={review.id} review={review} />
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <Card>
                                                        <CardContent className="p-8 text-center">
                                                            {showNegativeOnly ? (
                                                                <>
                                                                    <ThumbsUp className="w-12 h-12 text-green-300 mx-auto mb-4" />
                                                                    <p className="text-muted-foreground">Aucun avis négatif pour {monthName} ! 🎉</p>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                                                    <p className="text-muted-foreground">Aucun avis pour {monthName}</p>
                                                                </>
                                                            )}
                                                        </CardContent>
                                                    </Card>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                            </>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default MarketingPage;
